// Расширенный API клиент с поддержкой всех методов из оригинального клиента
// Адаптирован для использования платформо-независимых адаптеров

import { StorageAdapter, NetworkAdapter, TelegramAdapter } from '../platform/types';
import { ApiClientConfig } from './types';
import { Transaction, Category, Goal, Analytics, Settings, Limit, RecurringGoalDeposit, PaymentMethod, Subscription, RecurringIncomeDefinition, RecurringExpense, RecurringTransaction, UserNotification, Announcement } from '../types';

export interface Session {
  id: number;
  device_type: 'mobile' | 'desktop' | 'tablet' | 'unknown';
  device_name: string;
  ip_address: string;
  created_at: string;
  last_activity: string;
  expires_at: string;
  is_active: boolean;
  is_current: boolean;
}

// SyncManagerInterface оставлен для обратной совместимости типов
export interface SyncManagerInterface {
  isOnline(): Promise<boolean>;
  sync(): Promise<void>;
  getTransactionsOffline(): Promise<any[]>;
  createTransactionOffline(transaction: any): Promise<any>;
  updateTransactionOffline(id: number, updates: any): Promise<void>;
  deleteTransactionOffline(id: number): Promise<void>;
}

// Базовый класс API клиента (будет расширяться)
export class ApiClient {
  private token: string | null = null;
  private refreshToken: string | null = null;
  private isRefreshing: boolean = false;
  private refreshPromise: Promise<boolean> | null = null;
  private initData: string = '';
  private config: ApiClientConfig;
  private storage: StorageAdapter;
  private network: NetworkAdapter;
  private telegram?: TelegramAdapter;
  private authPromise: Promise<void> | null = null;
  private tokenLoadPromise: Promise<void>;

  constructor(
    config: ApiClientConfig,
    storage: StorageAdapter,
    network: NetworkAdapter,
    telegram?: TelegramAdapter
  ) {
    // Проверяем, что apiBaseUrl установлен
    if (!config.apiBaseUrl || config.apiBaseUrl === '') {
      console.warn('⚠️ apiBaseUrl не установлен, используем http://localhost:3001');
      config.apiBaseUrl = 'http://localhost:3001';
    }
    console.log('🔧 [ApiClient] Initializing with apiBaseUrl:', config.apiBaseUrl);
    
    this.config = config;
    this.storage = storage;
    this.network = network;
    this.telegram = telegram;
    
    // Получаем initData из Telegram адаптера (если есть)
    if (this.telegram?.hasTelegram()) {
      this.initData = this.telegram.getInitData() || '';
    }
    
    // Загружаем сохраненный токен (промис сохраняем для ожидания в request())
    this.tokenLoadPromise = this.loadToken();
  }

  // setSyncManager оставлен как no-op для обратной совместимости
  setSyncManager(_syncManager: SyncManagerInterface) {
    // PowerSync заменяет syncManager — этот метод больше не нужен
  }

  private async loadToken(): Promise<void> {
    const savedToken = await this.storage.getItem('auth_token');
    if (savedToken) {
      this.token = savedToken;
    }
    const savedRefresh = await this.storage.getItem('refresh_token');
    if (savedRefresh) {
      this.refreshToken = savedRefresh;
    }
  }

  async setToken(token: string): Promise<void> {
    this.token = token;
    await this.storage.setItem('auth_token', token);
  }

  async setRefreshToken(refreshToken: string): Promise<void> {
    this.refreshToken = refreshToken;
    await this.storage.setItem('refresh_token', refreshToken);
  }

  async setTokens(token: string, refreshToken: string): Promise<void> {
    await this.setToken(token);
    await this.setRefreshToken(refreshToken);
  }

  private async attemptTokenRefresh(): Promise<boolean> {
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }
    
    if (!this.refreshToken) return false;
    
    this.isRefreshing = true;
    this.refreshPromise = (async () => {
      try {
        const response = await fetch(`${this.config.apiBaseUrl}/api/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: this.refreshToken }),
        });
        
        if (!response.ok) return false;
        
        const data = await response.json();
        if (data.token && data.refresh_token) {
          await this.setTokens(data.token, data.refresh_token);
          return true;
        }
        return false;
      } catch {
        return false;
      } finally {
        this.isRefreshing = false;
        this.refreshPromise = null;
      }
    })();
    
    return this.refreshPromise;
  }

  async getToken(): Promise<string | null> {
    await this.tokenLoadPromise;
    if (this.token) {
      return this.token;
    }
    const savedToken = await this.storage.getItem('auth_token');
    if (savedToken) {
      this.token = savedToken;
      return savedToken;
    }
    return null;
  }

  getApiBaseUrl(): string {
    return this.config.apiBaseUrl;
  }

  private async ensureDevAuth(): Promise<void> {
    // Если уже есть токен или initData, ничего не делаем
    if (this.token || this.initData) {
      return;
    }
    
    // Если уже идет процесс авторизации, ждем его
    if (this.authPromise) {
      return this.authPromise;
    }
    
    // Если не dev режим, ничего не делаем
    if (!this.config.isDevelopment) {
      return;
    }
    
    // Запускаем авторизацию
    this.authPromise = (async () => {
      try {
        const response = await fetch(`${this.config.apiBaseUrl}/api/dev-auth`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.token) {
            await this.setToken(data.token);
            console.log('🔧 [DEV MODE] Auto-authenticated with dev token');
          }
        }
      } catch (error: any) {
        // Если это network ошибка (сервер недоступен), не выводим как ошибку
        // Это нормально в dev режиме, если сервер не запущен
        const isNetworkError = error?.message?.includes('Network request failed') ||
                              error?.message?.includes('Failed to fetch') ||
                              error?.message?.includes('network');
        
        if (isNetworkError) {
          // Выводим только debug сообщение, не ошибку
          console.debug('🔧 [DEV MODE] Dev server not available (this is OK if server is not running)');
        } else {
          // Другие ошибки выводим как обычно
          console.error('🔧 [DEV MODE] Auto-auth failed:', error);
        }
      } finally {
        this.authPromise = null;
      }
    })();
    
    return this.authPromise;
  }

  private async request<T>(endpoint: string, options: RequestInit = {}, retryCount = 0): Promise<T> {
    // Ждём завершения загрузки токенов из storage (инициализация конструктора)
    await this.tokenLoadPromise;

    // 🔧 DEV MODE: Убеждаемся что авторизованы перед запросом
    await this.ensureDevAuth();
    
    // Обновляем initData перед использованием (Telegram WebApp может загрузиться позже)
    if (!this.token && this.telegram?.hasTelegram()) {
      const freshInitData = this.telegram.getInitData();
      if (freshInitData) {
        this.initData = freshInitData;
      }
    }

    const tokenUsedInThisRequest = this.token;
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      ...(options.headers as Record<string, string>),
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    } else if (this.initData) {
      headers['X-Telegram-Init-Data'] = this.initData;
    }

    try {
      // Проверяем, что apiBaseUrl установлен перед запросом
      if (!this.config.apiBaseUrl || this.config.apiBaseUrl === '') {
        console.error('❌ apiBaseUrl пустой! Используем http://localhost:3001');
        this.config.apiBaseUrl = 'http://localhost:3001';
      }
      
      const fullUrl = `${this.config.apiBaseUrl}${endpoint}`;
      const startTime = Date.now();
      console.log('🌐 [API Request]', fullUrl, retryCount > 0 ? `(retry ${retryCount})` : '');
      
      // Определяем платформу для оптимизации таймаутов
      const isMobile = typeof navigator !== 'undefined' && navigator.product === 'ReactNative';
      
      // Создаем timeout для запроса
      // Для сканирования чеков: увеличенный таймаут (90/75 секунд) - OpenAI Vision медленный
      // Мобильные сети: более агрессивные таймауты (25/15 секунд)
      // Веб/десктоп: оригинальные таймауты (45/30 секунд)
      const isReceiptScan = endpoint.includes('receipt/scan');
      const timeoutMs = isReceiptScan
        ? (retryCount > 0 ? 75000 : 90000)  // 90/75 сек для чеков (OpenAI может быть очень медленным)
        : isMobile 
          ? (retryCount > 0 ? 15000 : 25000)
          : (retryCount > 0 ? 30000 : 45000);
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        console.error(`⏱️ [API Request] Timeout after ${timeoutMs}ms`);
        controller.abort();
      }, timeoutMs);
      
      try {
        const response = await fetch(fullUrl, {
          ...options,
          headers,
          signal: controller.signal,
        });
        
        clearTimeout(timeoutId);
        const elapsed = Date.now() - startTime;
        console.log(`📥 [API Response] ${response.status} in ${elapsed}ms`);

        if (!response.ok) {
          console.log('⚠️ [API Response] Response not OK, parsing error...');
          const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
          
          // Проверяем ошибку 401 (неавторизован)
          // НО: для эндпоинтов авторизации (login, register) 401 означает неверные учетные данные,
          // а не истекшую сессию, поэтому не обрабатываем их как session expired
          const isAuthEndpoint = endpoint.includes('/api/auth/login') || 
                                 endpoint.includes('/api/auth/register') ||
                                 endpoint.includes('/api/auth/google') ||
                                 endpoint.includes('/api/auth/apple');
          
          if (response.status === 401 && !isAuthEndpoint) {
            const hadToken = tokenUsedInThisRequest !== null;
            
            if (!hadToken) {
              console.log('ℹ️ [API Info] 401 Unauthorized (no token provided)');
              throw new Error(errorData.error || errorData.message || 'Unauthorized');
            }
            
            // Try to refresh the access token before giving up
            if (retryCount === 0 && this.refreshToken) {
              console.log('🔄 [API] Access token expired, attempting refresh...');
              const refreshed = await this.attemptTokenRefresh();
              if (refreshed) {
                console.log('✅ [API] Token refreshed, retrying request');
                return this.request<T>(endpoint, options, retryCount + 1);
              }
              console.log('❌ [API] Refresh failed, session expired');
            }
            
            // A concurrent refresh may have replaced the token while our retry
            // was in flight, making the token we sent stale. If that happened,
            // retry with the latest token instead of logging the user out.
            if (retryCount > 0 && retryCount < 3 && this.token && this.token !== tokenUsedInThisRequest) {
              console.log('🔄 [API] Token changed by concurrent refresh, retrying with latest token');
              return this.request<T>(endpoint, options, retryCount + 1);
            }
            
            console.error('❌ [API Error]', response.status, errorData);
            
            this.token = null;
            this.refreshToken = null;
            await this.storage.removeItem('auth_token');
            await this.storage.removeItem('refresh_token');
            
            console.log('🔐 Session expired or revoked');
            
            let message = 'Session expired';
            
            if (errorData.code === 'TOKEN_INVALIDATED') {
              message = 'Password was changed';
            } else if (errorData.message === 'Session revoked or expired!') {
              message = 'Session revoked or expired';
            }
            
            if (this.config.onSessionExpired) {
              this.config.onSessionExpired(message);
            }
            
            throw new Error('Session expired');
          }
          
          // Для всех остальных ошибок (включая 401 на эндпоинтах авторизации) создаем обычную ошибку
          // Голосовой парсинг 400 — ожидаемо (пользователь сказал не транзакцию), не ERROR
          const isVoiceParseFail = endpoint.includes('/api/voice/parse') && response.status === 400 &&
            (errorData?.error === 'Could not parse transaction' || errorData?.details?.includes('LLM could not extract'));
          if (response.status !== 401 || isAuthEndpoint) {
            if (isVoiceParseFail) {
              console.warn('⚠️ [API] Voice parse: could not extract transaction from text');
            } else {
              console.error('❌ [API Error]', response.status, errorData);
            }
          }
          const error: any = new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
          error.response = {
            status: response.status,
            data: errorData
          };
          throw error;
        }

        // 204 No Content — это валидный успешный ответ без тела
        if (response.status === 204) {
          console.log(`✅ [API Success] ${endpoint} (no content)`);
          return {} as T;
        }

        // Проверяем, что ответ содержит JSON
        const contentType = response.headers.get('content-type');
        console.log('📋 [API Response] Content-Type:', contentType);
        
        if (!contentType || !contentType.includes('application/json')) {
          const text = await response.text();
          console.error('❌ [API Error] Non-JSON response:', text.substring(0, 100));
          throw new Error(`Server returned non-JSON response: ${text.substring(0, 100)}`);
        }

        const responseText = await response.text();
        if (!responseText || responseText.trim() === '') {
          // Некоторые эндпоинты возвращают 200 с пустым body
          console.log(`✅ [API Success] ${endpoint} (empty body)`);
          return {} as T;
        }

        let data: T;
        try {
          data = JSON.parse(responseText) as T;
        } catch (parseError) {
          console.error('❌ [API Error] Failed to parse JSON response:', responseText.substring(0, 200));
          throw parseError;
        }
        console.log(`✅ [API Success] ${endpoint} in ${Date.now() - startTime}ms`);
        return data;
      } catch (fetchError: any) {
        clearTimeout(timeoutId);
        const elapsed = Date.now() - startTime;
        
        // Проверяем, это timeout или другая ошибка
        if (fetchError.name === 'AbortError') {
          console.error(`⏱️ [API Timeout] ${endpoint} after ${elapsed}ms`);
          
          // Retry logic - максимум 2 попытки
          if (retryCount < 2) {
            console.log(`🔄 Retrying request (${retryCount + 1}/2)...`);
            await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1))); // Exponential backoff
            return this.request<T>(endpoint, options, retryCount + 1);
          }
          
          throw new Error(`Timeout: Server took too long to respond (${elapsed}ms)`);
        }
        
        // Другие ошибки fetch (network error). Голосовой парсинг 400 — ожидаемо, не ERROR.
        const isVoiceParseFail = endpoint.includes('/api/voice/parse') && fetchError.message?.includes('Could not parse transaction');
        if (isVoiceParseFail) {
          console.warn(`⚠️ [API] Voice parse failed after ${elapsed}ms`);
        } else {
          console.error(`❌ [API Error] ${endpoint} failed after ${elapsed}ms:`, fetchError.message);
        }
        
        // Retry на network error (1 раз)
        if (retryCount === 0 && (fetchError.message.includes('Network') || fetchError.message.includes('Failed to fetch'))) {
          console.log('🔄 Retrying on network error...');
          await new Promise(resolve => setTimeout(resolve, 2000));
          return this.request<T>(endpoint, options, retryCount + 1);
        }
        
        throw fetchError;
      }
    } catch (error: any) {
      const isVoiceParseFail = error?.message?.includes('Could not parse transaction');
      if (isVoiceParseFail) {
        console.warn('⚠️ [API] Voice parse:', error.message);
      } else {
        console.error('❌ [API Error]', error.message || error);
      }
      throw error;
    }
  }

  private async uploadFile<T>(endpoint: string, formData: FormData): Promise<T> {
    await this.tokenLoadPromise;

    // 🔧 DEV MODE: Убеждаемся что авторизованы перед запросом
    await this.ensureDevAuth();
    
    // Обновляем initData перед использованием (Telegram WebApp может загрузиться позже)
    if (!this.token && this.telegram?.hasTelegram()) {
      const freshInitData = this.telegram.getInitData();
      if (freshInitData) {
        this.initData = freshInitData;
      }
    }
    
    const headers: Record<string, string> = {};

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    } else if (this.initData) {
      headers['X-Telegram-Init-Data'] = this.initData;
    }

    // НЕ устанавливаем Content-Type для FormData - браузер/React Native сам установит правильный с boundary
    // Если установить вручную, это может сломать загрузку файла

    try {
      const response = await fetch(`${this.config.apiBaseUrl}${endpoint}`, {
        method: 'POST',
        headers,
        body: formData,
      });

      // Сначала получаем текст ответа (Response можно прочитать только один раз)
      const responseText = await response.text();
      
      if (!response.ok) {
        let errorData;
        try {
          // Пробуем распарсить как JSON
          errorData = JSON.parse(responseText);
        } catch {
          // Если не удалось распарсить JSON, используем текст
          errorData = { error: responseText || `HTTP ${response.status}` };
        }
        
        const error: any = new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
        error.response = {
          status: response.status,
          data: errorData
        };
        throw error;
      }

      // Парсим успешный ответ
      const data = JSON.parse(responseText);
      return data;
    } catch (error: any) {
      // Если это уже наша ошибка с response, пробрасываем дальше
      if (error.response) {
        throw error;
      }
      // Иначе оборачиваем в более понятную ошибку
      throw new Error(error.message || 'Ошибка загрузки файла');
    }
  }

  // Generic HTTP methods
  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // Auth
  async validateAndGetToken() {
    // Обновляем initData перед использованием (Telegram WebApp может загрузиться позже)
    if (this.telegram?.hasTelegram()) {
      const freshInitData = this.telegram.getInitData();
      if (freshInitData) {
        this.initData = freshInitData;
      }
    }
    
    // Проверяем, что initData не пустой
    if (!this.initData || this.initData.trim() === '') {
      throw new Error('Telegram initData is missing. Please open the app from Telegram.');
    }
    
    const response = await this.request<{ status: string; token: string; refresh_token?: string; user_id: number }>('/api/validate', {
      method: 'POST',
      body: JSON.stringify({ initData: this.initData }),
    });
    if (response.status === 'ok' && response.token) {
      await this.setToken(response.token);
      if (response.refresh_token) await this.setRefreshToken(response.refresh_token);
    }
    return response;
  }

  async register(firstName: string, email: string, password: string, currency?: string, language?: string, timezone?: string, timezone_city?: string): Promise<{ token: string; user_id: number; status: string }> {
    try {
      const fullUrl = `${this.config.apiBaseUrl}/api/auth/register`;
      console.log('🌐 [API Request] REGISTER:', fullUrl);
      console.log('📦 [API Request] Data:', { firstName, email, password: '***', currency, language });
      console.log('📤 [API Request] Starting fetch...');
      
      // Определяем платформу для оптимизации таймаутов
      const isMobile = typeof navigator !== 'undefined' && navigator.product === 'ReactNative';
      
      // Создаем timeout для запроса
      // Мобильные: 25 секунд, Веб: 30 секунд (регистрация может быть медленной)
      const timeoutMs = isMobile ? 25000 : 30000;
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        console.error('⏱️ [API Request] Timeout after', timeoutMs, 'ms');
        controller.abort();
      }, timeoutMs);
      
      try {
        const requestBody: any = { firstName, email, password };
        if (currency) requestBody.currency = currency;
        if (language) requestBody.language = language;
        if (timezone) requestBody.timezone = timezone;
        if (timezone_city) requestBody.timezone_city = timezone_city;
        
        const response = await fetch(fullUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody),
          signal: controller.signal,
        });

        clearTimeout(timeoutId);
        console.log('📥 [API Response] Received:', response.status, response.statusText);

        if (!response.ok) {
          console.log('⚠️ [API Response] Response not OK, parsing error...');
          const errorData = await response.json().catch(() => ({ error: 'Ошибка регистрации' }));
          console.error('❌ [API Error] Register failed:', errorData);
          throw new Error(errorData.error || 'Неизвестная ошибка');
        }

        console.log('📦 [API Response] Parsing JSON...');
        const data = await response.json();
        console.log('✅ [API Response] Register success:', { token: '***', user_id: data.user_id });
        
        if (data.token) {
          await this.setToken(data.token);
          if (data.refresh_token) await this.setRefreshToken(data.refresh_token);
        }
        
        return data;
      } catch (fetchError: any) {
        clearTimeout(timeoutId);
        
        if (fetchError.name === 'AbortError') {
          console.error('⏱️ [API Error] Request timeout');
          throw new Error('Timeout: Server took too long to respond');
        }
        
        throw fetchError;
      }
    } catch (error: any) {
      console.error('❌ [API Error] Register exception:', error.message || error);
      throw error;
    }
  }

  // Методы для прямого обращения к серверу
  async getTransactionsFromServer(period?: string, startDate?: string, endDate?: string) {
    const params = new URLSearchParams();
    if (period) params.append('period', period);
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    
    return this.request<{ transactions: any[] }>(`/api/transactions?${params.toString()}`);
  }

  async createTransactionOnServer(transaction: any) {
    return this.request<{ success: boolean; transaction: any; transaction_id: number }>('/api/transaction', {
      method: 'POST',
      body: JSON.stringify(transaction),
    });
  }

  async updateTransactionOnServer(id: number, transaction: any) {
    return this.request<{ success: boolean; transaction: any }>(`/api/transaction/${id}`, {
      method: 'PUT',
      body: JSON.stringify(transaction),
    });
  }

  async deleteTransactionOnServer(id: number) {
    return this.request<{ success: boolean }>(`/api/transaction/${id}?type=expense`, {
      method: 'DELETE',
    });
  }

  // Дополнительные методы будут добавлены постепенно
  // Все методы из оригинального API клиента будут перенесены сюда
  // с использованием адаптеров вместо прямых вызовов платформо-специфичных API

  // ===============================================
  // Методы с поддержкой офлайн-режима
  // ===============================================

  async getTransactions(period?: string, startDate?: string, endDate?: string) {
    // PowerSync обрабатывает offline-данные через локальную SQLite
    // ApiClient теперь чистый HTTP-клиент
    return this.getTransactionsFromServer(period, startDate, endDate);
  }

  async getTransactionsCustom(startDate: string, endDate: string) {
    return this.getTransactions('custom', startDate, endDate);
  }

  async getInternalTransfers(startDate: string, endDate: string) {
    const params = new URLSearchParams();
    params.append('period', 'custom');
    params.append('start_date', startDate);
    params.append('end_date', endDate);
    
    return this.request<{ transactions: Transaction[] }>(`/api/transactions/internal-transfers?${params.toString()}`);
  }

  async addTransaction(transaction: Omit<Transaction, 'id' | 'web_id'>) {
    // PowerSync обрабатывает offline через uploadCrudEntry
    return this.request<{ success: boolean; transaction: Transaction; web_id: string }>('/api/transaction', {
      method: 'POST',
      body: JSON.stringify(transaction),
    });
  }

  async updateTransaction(id: number, transaction: Partial<Transaction> & { type: string }) {
    return this.request<{ success: boolean; transaction: Transaction }>(`/api/transaction/${id}`, {
      method: 'PUT',
      body: JSON.stringify(transaction),
    });
  }

  async deleteTransaction(id: number, type: string) {
    return this.request<{ success: boolean }>(`/api/transaction/${id}?type=${type}`, {
      method: 'DELETE',
    });
  }

  // Summary
  async getSummary(period?: string, startDate?: string, endDate?: string) {
    const params = new URLSearchParams();
    if (period) params.append('period', period);
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    
    try {
      const result = await this.request<{
        total_income: number;
        total_expense: number;
        balance: number;
        total_current_balance: number;
        currency_symbol: string;
      }>(`/api/summary?${params.toString()}`);
      
      // Кэшируем баланс
      if (result) {
        await this.storage.setItem('cached_summary', JSON.stringify(result));
      }
      return result;
    } catch (error) {
      // При ошибке используем кэш
      const cached = await this.storage.getItem('cached_summary');
      if (cached) {
        console.log('⚠️ Ошибка сервера: используем закэшированный баланс');
        return JSON.parse(cached);
      }
      throw error;
    }
  }

  // Categories
  async getCategories() {
    return this.request<{ categories: Category[] }>('/api/categories');
  }

  async getAllCategories() {
    try {
      const result = await this.request<{ 
        system_categories: Array<{ db_name: string; loc_key: string; emoji: string }>;
        user_categories: string[];
      }>('/api/all-categories');
      
      // Кэшируем категории
      if (result) {
        await this.storage.setItem('cached_categories', JSON.stringify(result));
      }
      return result;
    } catch (error) {
      // При ошибке (офлайн и т.д.) используем закэшированные категории
      const cached = await this.storage.getItem('cached_categories');
      if (cached) {
        console.log('📴 Используем закэшированные категории');
        return JSON.parse(cached);
      }
      throw error;
    }
  }

  async addCategory(name: string, options?: { parent_id?: number; emoji?: string; category_level?: string }) {
    return this.request<{ success: boolean; category: Category }>('/api/categories', {
      method: 'POST',
      body: JSON.stringify({
        name,
        ...(options?.parent_id != null && { parent_id: options.parent_id }),
        ...(options?.emoji && { emoji: options.emoji }),
        ...(options?.category_level && { category_level: options.category_level }),
      }),
    });
  }

  async renameCategory(oldName: string, newName: string) {
    return this.request<{ success: boolean }>(`/api/categories/${encodeURIComponent(oldName)}`, {
      method: 'PUT',
      body: JSON.stringify({ new_name: newName }),
    });
  }

  async renameCategoryById(categoryId: string | number, newName: string, parentId?: number | null, emoji?: string) {
    const body: { name?: string; parent_id?: number | null; emoji?: string } = {};
    if (newName != null && newName !== '') body.name = newName;
    if (parentId !== undefined) body.parent_id = parentId;
    if (emoji !== undefined) body.emoji = emoji;
    return this.request<{ success: boolean }>(`/api/categories/id/${categoryId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  async deleteCategory(name: string, targetCategoryId?: number) {
    return this.request<{ success: boolean }>(`/api/categories/${encodeURIComponent(name)}`, {
      method: 'DELETE',
      body: targetCategoryId ? JSON.stringify({ target_category_id: targetCategoryId }) : undefined,
    });
  }

  async deleteCategoryById(categoryId: number | string, targetCategoryId?: number | string) {
    return this.request<{ success: boolean }>(`/api/categories/id/${categoryId}`, {
      method: 'DELETE',
      body: targetCategoryId ? JSON.stringify({ target_category_id: Number(targetCategoryId) }) : undefined,
    });
  }

  async suggestCategory(title: string) {
    return this.request<{ success: boolean; category: string | null }>('/api/category/suggest', {
      method: 'POST',
      body: JSON.stringify({ title }),
    });
  }

  // Goals
  async getGoals() {
    const response = await this.request<{ 
      limits: any[]; 
      monthly_expenses: any[]; 
      goals: Goal[];
      main_balance: number;
      available_categories_for_limit: string[];
    }>('/api/limits-and-goals');
    return { goals: response.goals };
  }

  async addGoal(goal: Omit<Goal, 'id' | 'current_amount' | 'created_at'>) {
    return this.request<{ message: string; goal_id: number; goal?: Goal }>('/api/goals', {
      method: 'POST',
      body: JSON.stringify({ 
        name: goal.name, 
        icon: goal.icon, 
        target_amount: goal.target_amount,
        deadline: goal.deadline,
      }),
    });
  }

  async updateGoal(goalId: string, updates: { name?: string; icon?: string; target_amount?: number | null; deadline?: string | null }) {
    return this.request<{ message: string; goals: Goal[] }>('/api/goals/update', {
      method: 'PUT',
      body: JSON.stringify({ 
        goal_id: parseInt(goalId), 
        name: updates.name,
        icon: updates.icon,
        target_amount: updates.target_amount,
        deadline: updates.deadline
      }),
    });
  }

  async depositToGoal(goalId: string, amount: number, paymentMethodId?: string) {
    // Валидация goalId
    const parsedGoalId = parseInt(goalId, 10);
    if (isNaN(parsedGoalId)) {
      throw new Error('Invalid goal ID');
    }

    // Валидация paymentMethodId если предоставлен
    let parsedPaymentMethodId: number | undefined;
    if (paymentMethodId) {
      parsedPaymentMethodId = parseInt(paymentMethodId, 10);
      if (isNaN(parsedPaymentMethodId)) {
        throw new Error('Invalid payment method ID');
      }
    }

    // Формируем тело запроса, включая payment_method_id только если он указан
    const requestBody: { goal_id: number; amount: number; payment_method_id?: number } = {
      goal_id: parsedGoalId,
      amount,
    };
    
    // Добавляем payment_method_id только если он валиден
    if (parsedPaymentMethodId !== undefined) {
      requestBody.payment_method_id = parsedPaymentMethodId;
    }

    return this.request<{ message: string; goals: Goal[]; main_balance: number; payment_method_balance?: number | null }>('/api/goals/deposit', {
      method: 'POST',
      body: JSON.stringify(requestBody),
    });
  }

  async closeGoal(goalId: string, paymentMethodId: string | null = null) {
    return this.request<{ message: string; goals: Goal[]; main_balance: number }>('/api/goals/close', {
      method: 'POST',
      body: JSON.stringify({ 
        goal_id: parseInt(goalId),
        payment_method_id: paymentMethodId ? parseInt(paymentMethodId) : null,
      }),
    });
  }

  async createRecurringGoalDeposit(data: {
    goal_id: number;
    title: string;
    amount: number;
    frequency: string;
    day_of_week?: number;
    day_of_month?: number;
    start_date?: string;
    notification_time?: string;
    payment_method_id?: number;
    timezone?: string; // Явно передаем timezone устройства
  }) {
    return this.request<{ message: string; deposit_id: number }>('/api/goals/deposit/recurring', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateRecurringGoalDeposit(depositId: string, data: {
    title?: string;
    amount?: number;
    frequency?: string;
    day_of_week?: number;
    day_of_month?: number;
    start_date?: string;
    notification_time?: string;
    next_deposit_date?: string;
  }) {
    return this.request<{ message: string; deposit_id: number }>(`/api/goals/deposit/recurring/${depositId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteGoal(goalId: string) {
    return this.request<{ message: string }>('/api/goals/delete', {
      method: 'POST',
      body: JSON.stringify({ goal_id: parseInt(goalId) }),
    });
  }

  async togglePauseGoal(goalId: string) {
    return this.request<{ message: string; status: string }>('/api/goals/toggle-pause', {
      method: 'POST',
      body: JSON.stringify({ goal_id: parseInt(goalId) }),
    });
  }

  async restoreGoal(goalId: string) {
    return this.request<{ message: string; goals: Goal[]; main_balance: number }>('/api/goals/restore', {
      method: 'POST',
      body: JSON.stringify({ goal_id: parseInt(goalId) }),
    });
  }

  // Announcements
  async getAnnouncements() {
    return await this.request<{ success: boolean; announcements: Announcement[] }>('/api/announcements');
  }

  // Notifications
  async getNotifications() {
    return await this.request<{ success: boolean; notifications: UserNotification[]; count: number }>('/api/notifications');
  }

  async confirmNotification(notificationId: number) {
    return this.request<{ success: boolean; message: string; goals: Goal[]; main_balance: number; notifications: UserNotification[] }>(
      `/api/notifications/${notificationId}/confirm`,
      { method: 'POST' }
    );
  }

  async skipNotification(notificationId: number) {
    return this.request<{ success: boolean; message: string; notifications: UserNotification[] }>(
      `/api/notifications/${notificationId}/skip`,
      { method: 'POST' }
    );
  }

  async checkNotifications() {
    return this.request<{ success: boolean; message: string; created: number }>('/api/notifications/check', {
      method: 'POST',
    });
  }

  // Recurring Goal Deposits
  async getRecurringGoalDeposits() {
    return this.request<{ deposits: RecurringGoalDeposit[] }>('/api/goals/deposits/recurring');
  }

  async deleteRecurringGoalDeposit(depositId: string) {
    return this.request<{ message: string }>(`/api/goals/deposits/recurring/${depositId}`, {
      method: 'DELETE',
    });
  }

  async toggleRecurringGoalDeposit(depositId: string) {
    return this.request<{ message: string; is_active: boolean }>(`/api/goals/deposits/recurring/${depositId}/toggle`, {
      method: 'POST',
    });
  }

  // Payment Methods
  async getPaymentMethods() {
    return this.request<{ payment_methods: PaymentMethod[] }>('/api/payment-methods');
  }

  async createPaymentMethod(name: string, type: string = 'general', balance: number = 0, subtype?: string) {
    return this.request<{ message: string; payment_method: PaymentMethod }>('/api/payment-methods', {
      method: 'POST',
      body: JSON.stringify({ name, type, balance, subtype }),
    });
  }

  async updatePaymentMethodBalance(methodId: string, balance: number) {
    return this.request<{ message: string; payment_method: PaymentMethod }>(`/api/payment-methods/${methodId}/balance`, {
      method: 'PUT',
      body: JSON.stringify({ balance }),
    });
  }

  async setDefaultPaymentMethod(methodId: string, isDefault: boolean = true) {
    return this.request<{ message: string; payment_method: PaymentMethod }>(`/api/payment-methods/${methodId}/default`, {
      method: 'PUT',
      body: JSON.stringify({ is_default: isDefault }),
    });
  }

  async renamePaymentMethod(methodId: string, name: string) {
    return this.request<{ message: string; payment_method: PaymentMethod }>(`/api/payment-methods/${methodId}`, {
      method: 'PUT',
      body: JSON.stringify({ name }),
    });
  }

  async updatePaymentMethodType(methodId: string, subtype: string, name: string) {
    return this.request<{ message: string; payment_method: PaymentMethod }>(`/api/payment-methods/${methodId}`, {
      method: 'PUT',
      body: JSON.stringify({ subtype, name }),
    });
  }

  async deletePaymentMethod(methodId: string, targetAccountId?: string) {
    const url = targetAccountId 
      ? `/api/payment-methods/${methodId}?targetAccountId=${targetAccountId}`
      : `/api/payment-methods/${methodId}`;
    return this.request<{ 
      message: string; 
      transferredBalance?: number; 
      targetAccountId?: string;
      error?: string;
      requiresTransfer?: boolean;
    }>(url, {
      method: 'DELETE',
    });
  }

  // Subscription
  async getSubscription() {
    return this.request<{ subscription: Subscription }>('/api/subscription');
  }

  async updateSubscriptionEmail(email: string) {
    return this.request<{ success: boolean; message: string }>('/api/subscription/email', {
      method: 'PUT',
      body: JSON.stringify({ email }),
    });
  }

  async cancelSubscriptionAutorenew() {
    return this.request<{ success: boolean; message: string }>('/api/subscription/cancel-autorenew', {
      method: 'POST',
    });
  }

  async enableSubscriptionAutorenew() {
    return this.request<{ success: boolean; message: string }>('/api/subscription/enable-autorenew', {
      method: 'POST',
    });
  }

  async validatePromoCode(promoCode: string) {
    return this.request<{ valid: boolean; promo: any }>('/api/subscription/validate-promo', {
      method: 'POST',
      body: JSON.stringify({ promo_code: promoCode }),
    });
  }

  async applyPromoCode(promoCode: string) {
    return this.request<{ success: boolean; message: string; promo: any }>('/api/subscription/apply-promo', {
      method: 'POST',
      body: JSON.stringify({ promo_code: promoCode }),
    });
  }

  async getSubscriptionPrices(currency?: string) {
    const url = currency ? `/api/subscription/prices?currency=${currency}` : '/api/subscription/prices';
    return this.request<{ plans: any[]; currency: string }>(url);
  }

  async createPaymentLink(planCode: string, currency: string) {
    return this.request<{ payment_url: string }>('/api/subscription/create-payment', {
      method: 'POST',
      body: JSON.stringify({ plan_code: planCode, currency }),
    });
  }

  async createTelegramPaymentDeepLink(planCode: string): Promise<{
    success: boolean;
    telegram_link: string;
    payment_token: string;
    expires_in: number;
  }> {
    return this.request('/api/subscription/create-telegram-payment-deeplink', {
      method: 'POST',
      body: JSON.stringify({ plan_code: planCode })
    });
  }

  async checkTelegramPaymentStatus(token: string): Promise<{
    account_linked: boolean;
    processed: boolean;
  }> {
    return this.request(`/api/subscription/check-telegram-payment-status/${token}`);
  }

  // Limits
  async getLimitsAndGoalsData() {
    return this.request<{ 
      limits: Limit[];
      monthly_expenses: Array<{ category: string; amount: number }>;
      goals: Goal[];
      main_balance: number;
      available_categories_for_limit: string[];
    }>('/api/limits-and-goals');
  }

  async getLimits() {
    const response = await this.getLimitsAndGoalsData();
    return { limits: response.limits, monthly_expenses: response.monthly_expenses, available_categories: response.available_categories_for_limit };
  }

  async setLimit(category: string, amount: number, periodMonths: 1 | 3 | 6 | 12 = 1, categoryId?: number, startMonth?: string) {
    return this.request<{ 
      message: string;
      success: boolean;
      action: 'set' | 'deleted';
      limit?: {
        id: number;
        category_name: string;
        limit_amount: number;
        period_months: number;
        start_month?: string;
      };
    }>('/api/limits', {
      method: 'POST',
      body: JSON.stringify({ 
        category_name: category, 
        category_id: categoryId,
        limit_amount: amount,
        period_months: periodMonths,
        ...(startMonth ? { start_month: startMonth } : {}),
      }),
    });
  }

  async deleteLimit(category: string, periodMonths: 1 | 3 | 6 | 12 = 1) {
    // Удаление лимита через установку amount = 0
    return this.setLimit(category, 0, periodMonths);
  }

  // Analytics
  async getMonthlyAnalytics(year: number, month?: number) {
    const params = new URLSearchParams({ year: year.toString() });
    if (month) params.append('month', month.toString());
    
    return this.request<Analytics>(`/api/analytics/monthly?${params.toString()}`);
  }

  async getCategoryExpenses(period: string = 'month', startDate?: string, endDate?: string) {
    console.log('🌐 [API] getCategoryExpenses called with:', { period, startDate, endDate });
    
    try {
      // Если переданы start и end даты и период 'custom', формируем специальный формат периода для бэкенда
      let periodValue = period;
      if (period === 'custom' && startDate && endDate) {
        periodValue = `${startDate}_${endDate}`;
        console.log('🌐 [API] Using custom period format:', periodValue);
      }
      
      const params = new URLSearchParams();
      params.append('period', periodValue);
      
      const url = `/api/analytics/category-expenses?${params.toString()}`;
      console.log('🌐 [API] GET', url);
      
      const response = await this.request<{
        expenses?: Array<{ category: string; amount: number; percentage: number }>;
        category_expenses?: Record<string, number>;
        total_expense_for_period?: number;
      }>(url);
      
      // Получаем общую сумму расходов
      const totalExpense = response.total_expense_for_period || 0;
      
      // Если бэкенд вернул готовый массив expenses - используем его
      if (response.expenses && response.expenses.length > 0) {
        console.log('✅ [API] Using expenses array from backend, total:', totalExpense);
        return { expenses: response.expenses, totalExpense };
      }
      
      // Иначе преобразуем category_expenses из объекта в массив
      const expenses = Object.entries(response.category_expenses || {}).map(([category, amount]) => ({
        category,
        amount: Math.round(amount * 100) / 100,
        percentage: totalExpense > 0 ? Math.round((amount / totalExpense) * 10000) / 100 : 0
      }));
      
      console.log('✅ [API] Category expenses transformed:', expenses, 'total:', totalExpense);
      return { expenses, totalExpense };
    } catch (error) {
      console.error('❌ [API] getCategoryExpenses failed, returning empty:', error);
      // Возвращаем пустой массив, чтобы не ломать UI
      return { expenses: [], totalExpense: 0 };
    }
  }

  // Settings
  async getSettings() {
    try {
      const result = await this.request<{ settings: Settings }>('/api/settings');
      // Кэшируем настройки для офлайн-режима
      if (result?.settings) {
        await this.storage.setItem('cached_settings', JSON.stringify(result.settings));
      }
      return result;
    } catch (error: any) {
      // При ошибке (включая таймаут) пытаемся использовать кэш
      const cached = await this.storage.getItem('cached_settings');
      if (cached) {
        const isTimeout = error?.message?.includes('Timeout') || error?.message?.includes('timeout');
        console.log(`⚠️ ${isTimeout ? 'Таймаут' : 'Ошибка'} сервера: используем закэшированные настройки`);
        return { settings: JSON.parse(cached) };
      }
      // Если кэша нет, но это таймаут - возвращаем дефолтные настройки вместо ошибки
      if (error?.message?.includes('Timeout') || error?.message?.includes('timeout')) {
        console.log('⚠️ Таймаут и нет кэша: используем дефолтные настройки');
        return {
          settings: {
            currency: 'USD',
            language: 'ru',
            timezone: 'UTC',
            // НЕ устанавливаем subscription_status - он придет с сервера
          } as Settings
        };
      }
      throw error;
    }
  }

  async updateSettings(settings: Partial<Settings>) {
    try {
      const result = await this.request<{ success: boolean; settings: Settings }>('/api/settings', {
        method: 'PUT',
        body: JSON.stringify(settings),
      });
      // Обновляем кэш
      if (result?.settings) {
        await this.storage.setItem('cached_settings', JSON.stringify(result.settings));
      }
      return result;
    } catch (error) {
      // При ошибке сохраняем локально
      const cached = await this.storage.getItem('cached_settings');
      if (cached) {
        console.log('📴 Используем локальный кэш настроек');
        const currentSettings = JSON.parse(cached);
        const updatedSettings = { ...currentSettings, ...settings };
        await this.storage.setItem('cached_settings', JSON.stringify(updatedSettings));
        return { success: true, settings: updatedSettings };
      }
      throw error;
    }
  }

  async deleteAllData() {
    return this.request<{ success: boolean }>('/api/user-data', {
      method: 'DELETE',
    });
  }

  async deleteAccount(reason?: string) {
    return this.request<{ success: boolean }>('/api/account', {
      method: 'DELETE',
      body: reason ? JSON.stringify({ reason }) : undefined,
    });
  }

  // Sessions Management
  async getSessions() {
    return this.request<{ sessions: Session[] }>('/api/sessions');
  }

  async revokeSession(sessionId: number) {
    return this.request<{ success: boolean }>(`/api/sessions/${sessionId}`, {
      method: 'DELETE',
    });
  }

  async revokeAllOtherSessions() {
    return this.request<{ success: boolean; count: number }>('/api/sessions/all-other', {
      method: 'DELETE',
    });
  }

  async revokeCurrentSession() {
    return this.request<{ success: boolean }>('/api/sessions/current', {
      method: 'DELETE',
    });
  }

  // Recurring Incomes
  async getRecurringIncomes() {
    return this.request<{ definitions: RecurringIncomeDefinition[] }>('/api/recurring_income_definitions');
  }

  async getRecurringIncomeDefinitions() {
    return this.request<RecurringIncomeDefinition[]>('/api/recurring_income_definitions');
  }

  async addRecurringIncome(template: Omit<RecurringIncomeDefinition, 'id' | 'user_id' | 'web_id' | 'created_at' | 'is_active' | 'first_occurrence_date' | 'next_notification_date'>) {
    return this.request<{ success: boolean; definition: RecurringIncomeDefinition }>('/api/recurring_income_definition', {
      method: 'POST',
      body: JSON.stringify(template),
    });
  }

  async addRecurringIncomeDefinition(data: {
    title: string;
    default_amount: number;
    frequency: 'daily' | 'weekly' | 'monthly' | 'yearly';
    start_date: string; // ISO date
    notification_time?: string; // HH:MM, по умолчанию 09:00
    payment_method_id?: number; // ID счёта для зачисления
    reminder_times?: string[]; // Массив времен напоминаний
    reminder_offsets?: number[]; // Массив смещений (дней до события)
    reminders_enabled?: boolean; // Тогл напоминаний
    description?: string; // Описание
  }) {
    return this.request<RecurringIncomeDefinition>('/api/recurring_income_definitions', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateRecurringIncome(definitionId: string, updates: Partial<RecurringIncomeDefinition>) {
    return this.request<{ success: boolean }>(`/api/recurring_income_definition/${definitionId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async updateRecurringIncomeDefinition(definitionId: number, data: {
    title?: string;
    default_amount?: number;
    frequency?: 'daily' | 'weekly' | 'monthly' | 'yearly';
    start_date?: string;
    notification_time?: string;
    payment_method_id?: number; // ID счёта для зачисления
    is_active?: boolean;
    reminder_times?: string[];
    reminder_offsets?: number[];
    reminders_enabled?: boolean;
    description?: string;
  }) {
    return this.request<RecurringIncomeDefinition>(`/api/recurring_income_definition/${definitionId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteRecurringIncome(definitionId: string) {
    return this.request<{ success: boolean }>(`/api/recurring_income_definition/${definitionId}`, {
      method: 'DELETE',
    });
  }

  async deleteRecurringIncomeDefinition(definitionId: number, withHistory: boolean = false) {
    return this.request<{ success: boolean }>(`/api/recurring_income_definition/${definitionId}?withHistory=${withHistory}`, {
      method: 'DELETE',
    });
  }

  async toggleRecurringIncomeDefinition(definitionId: number) {
    return this.request<{ success: boolean; is_active: boolean }>(`/api/recurring_income_definition/${definitionId}/toggle`, {
      method: 'POST',
    });
  }

  // Recurring Expenses
  async getRecurringExpenses() {
    // Получаем все expenses где is_monthly != 2 (не разовые)
    return this.request<RecurringExpense[]>('/api/expenses?recurring_only=true');
  }

  async addRecurringExpense(data: {
    title: string;
    amount: number;
    category?: string;
    expense_type: 'weekly' | 'monthly' | 'yearly';
    start_date_recurring: string; // ISO date
    notification_time?: string; // HH:MM
    disable_payment_notifications?: boolean;
    payment_method_id?: number;
    goal_id?: number;
    reminder_times?: string[];
    reminder_offsets?: number[];
    reminders_enabled?: boolean;
    description?: string;
    next_notification_date?: string;
    timezone?: string;
  }) {
    // Конвертируем expense_type в is_monthly:
    // weekly -> 3, monthly -> 1, yearly -> 0
    const is_monthly = 
      data.expense_type === 'weekly' ? 3 :
      data.expense_type === 'monthly' ? 1 :
      data.expense_type === 'yearly' ? 0 : 1;

    const payload: Record<string, any> = {
      title: data.title,
      amount: data.amount,
      category: data.category,
      expense_details: {
        expense_type: data.expense_type,
        start_date_recurring: data.start_date_recurring,
      },
      notification_time: data.notification_time || '09:00',
      disable_payment_notifications: data.disable_payment_notifications || false,
      payment_method_id: data.payment_method_id,
      goal_id: data.goal_id,
      reminder_times: data.reminder_times,
      reminder_offsets: data.reminder_offsets,
      reminders_enabled: data.reminders_enabled,
      description: data.description,
    };
    if (data.next_notification_date) payload.next_notification_date = data.next_notification_date;
    if (data.timezone) payload.timezone = data.timezone;

    return this.request<RecurringExpense>('/api/expense', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateRecurringExpense(expenseId: number, data: {
    title?: string;
    amount?: number;
    category?: string;
    expense_type?: 'weekly' | 'monthly' | 'yearly';
    start_date_recurring?: string;
    notification_time?: string;
    disable_payment_notifications?: boolean;
    payment_method_id?: number;
    reminder_times?: string[];
    reminder_offsets?: number[];
    reminders_enabled?: boolean;
    description?: string;
    next_notification_date?: string;
  }) {
    const payload: Record<string, any> = {
      title: data.title,
      amount: data.amount,
      category: data.category,
      expense_details: data.expense_type && data.start_date_recurring ? {
        expense_type: data.expense_type,
        start_date_recurring: data.start_date_recurring,
      } : undefined,
      notification_time: data.notification_time,
      disable_payment_notifications: data.disable_payment_notifications,
      payment_method_id: data.payment_method_id,
      reminder_times: data.reminder_times,
      reminder_offsets: data.reminder_offsets,
      reminders_enabled: data.reminders_enabled,
      description: data.description,
    };
    if (data.next_notification_date) payload.next_notification_date = data.next_notification_date;
    return this.request<RecurringExpense>(`/api/expenses/${expenseId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async deleteRecurringExpense(expenseId: number, withHistory: boolean = false) {
    return this.request<{ success: boolean }>(`/api/expenses/${expenseId}?withHistory=${withHistory}`, {
      method: 'DELETE',
    });
  }

  async toggleRecurringExpense(expenseId: number) {
    return this.request<{ is_active: boolean }>(`/api/expenses/${expenseId}/toggle`, {
      method: 'POST',
    });
  }

  // Unified method to get all recurring transactions
  async getAllRecurringTransactions(): Promise<RecurringTransaction[]> {
    const [incomes, expenses] = await Promise.all([
      this.getRecurringIncomeDefinitions(),
      this.getRecurringExpenses(),
    ]);

    // Convert to unified format
    const unifiedIncomes: RecurringTransaction[] = incomes.map(income => ({
      id: income.id,
      web_id: income.web_id,
      type: 'income' as const,
      title: income.title,
      amount: income.default_amount,
      frequency: income.frequency,
      day_of_week: income.day_of_week,
      day_of_month: income.day_of_month,
      start_date: income.first_occurrence_date,
      next_date: income.next_notification_date,
      notification_time: income.notification_time,
      is_active: income.is_active,
      created_at: income.created_at,
      payment_method_id: income.payment_method_id, // 🔥 FIX: Добавляем счёт
      reminder_times: income.reminder_times,
      reminder_offsets: income.reminder_offsets,
      reminders_enabled: income.reminders_enabled,
      description: income.description,
    }));

    const unifiedExpenses: RecurringTransaction[] = expenses.map(expense => {
      const frequency = 
        expense.is_monthly === 0 ? 'yearly' :
        expense.is_monthly === 1 ? 'monthly' :
        expense.is_monthly === 3 ? 'weekly' : 'monthly';

      return {
        id: expense.id,
        web_id: expense.web_id,
        type: 'expense' as const,
        title: expense.title,
        amount: expense.amount,
        category: expense.category,
        frequency,
        day_of_week: expense.is_monthly === 3 ? expense.day : undefined,
        day_of_month: expense.is_monthly === 1 || expense.is_monthly === 0 ? expense.day : undefined,
        month: expense.month,
        start_date: expense.start_date_recurring || '',
        notification_time: expense.notification_time || '09:00',
        is_active: expense.is_active === true,
        created_at: expense.created_at,
        payment_method_id: expense.payment_method_id,
        goal_id: expense.goal_id,
        disable_payment_notifications: expense.disable_payment_notifications,
        reminder_times: expense.reminder_times,
        reminder_offsets: expense.reminder_offsets,
        reminders_enabled: expense.reminders_enabled,
        description: expense.description,
      };
    });

    return [...unifiedIncomes, ...unifiedExpenses].sort((a, b) => 
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  }

  // Voice Recognition
  async transcribeVoice(audioBlob: Blob) {
    const formData = new FormData();
    formData.append('audio', audioBlob, 'audio.webm');

    return this.uploadFile<{
      text: string;
    }>('/api/voice/transcribe', formData);
  }

  // Voice Recognition from Base64 (for mobile apps)
  async transcribeVoiceBase64(audioBase64: string, mimeType: string = 'audio/m4a') {
    return this.request<{
      success: boolean;
      text: string;
    }>('/api/voice/transcribe-base64', {
      method: 'POST',
      body: JSON.stringify({ 
        audio_base64: audioBase64,
        mime_type: mimeType
      }),
    });
  }

  // Parse voice transaction with LLM
  async parseVoiceTransaction(text: string) {
    return this.request<{
      success: boolean;
      transactions?: Array<{
        type: 'income' | 'expense';
        amount: number | null;
        currency?: string;
        item_name: string;
        category?: string | null;
        transaction_date?: string | null;
        is_recurring?: boolean;
        recurring_frequency?: 'daily' | 'weekly' | 'monthly' | 'yearly' | null;
        payment_method_name?: string | null;
      }>;
      // Поле совместимости со старым контрактом
      transaction?: any;
    }>('/api/voice/parse', {
      method: 'POST',
      body: JSON.stringify({ text }),
    });
  }

  // AI Insights
  async getInsights(period?: string, startDate?: string, endDate?: string, language?: string) {
    const params = new URLSearchParams();
    if (period) params.append('period', period);
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (language) params.append('language', language);
    
    const url = params.toString() ? `/api/insights?${params.toString()}` : '/api/insights';
    return this.request<{
      insights: any[];
      cached: boolean;
      generated_at: string;
      next_update?: string;
    }>(url);
  }

  // Receipt Scanner
  async scanReceipt(imageFile: File | Blob) {
    const formData = new FormData();
    formData.append('receipt_image', imageFile, 'receipt.jpg');

    return this.uploadFile<{
      success: boolean;
      transaction?: any;
      receipt_details?: {
        merchant_name?: string;
        date?: string;
        items?: any[];
        payment_method?: string;
        confidence?: number;
      };
      suggestions?: {
        category?: string;
      };
      error?: string;
    }>('/api/receipt/scan', formData);
  }

  // Receipt Scanner (base64 для React Native)
  async scanReceiptBase64(base64Image: string, mimetype: string = 'image/jpeg', filename: string = 'receipt.jpg') {
    return this.post<{
      success: boolean;
      transaction?: any;
      receipt_details?: {
        merchant_name?: string;
        date?: string;
        items?: any[];
        total_amount?: number;
        subtotal?: number;
        tax?: number;
        tax_rate?: number;
        tip?: number;
        service_fee?: number;
        discount?: number;
        payment_method?: string;
        confidence?: number;
      };
      suggestions?: {
        category?: string;
      };
      error?: string;
    }>('/api/receipt/scan-base64', {
      receipt_image_base64: base64Image,
      mimetype,
      filename,
    });
  }

  async getFrequentTransactions(limit?: number) {
    const url = limit ? `/api/frequent-transactions?limit=${limit}` : '/api/frequent-transactions';
    return await this.request<{
      frequent_categories: Array<{
        category: string;
        icon: string;
        count: number;
        avg_amount: number;
        frequent_titles: string[];
      }>;
    }>(url);
  }

  async getFrequentTitles(type: 'income' | 'expense', limit: number = 5) {
    return await this.request<{
      frequent_titles: Array<{
        title: string;
        category: string;
        count: number;
      }>;
    }>(`/api/frequent-titles?type=${type}&limit=${limit}`);
  }

  // AI Chat
  async getAIChatHistory(limit: number = 50, lang?: string) {
    const langParam = lang ? `&lang=${lang}` : '';
    return this.request<{
      success: boolean;
      messages: Array<{
        id: string;
        role: 'user' | 'assistant';
        content: string;
        timestamp: string;
        isOffTopic?: boolean;
      }>;
      contextualSuggestions?: string[];
      proactiveInsight?: string;
    }>(`/api/ai-chat/history?limit=${limit}${langParam}`, {
      method: 'GET',
    });
  }

  async sendAIChatMessage(message: string, deviceLanguage?: string, dbLanguage?: string, skipSaveUser?: boolean) {
    return this.request<{
      success: boolean;
      message: string;
      is_off_topic?: boolean;
      follow_ups?: string[];
      tokens_used?: number;
      detected_language?: string;
      error?: string;
    }>('/api/ai-chat', {
      method: 'POST',
      body: JSON.stringify({ 
        message, 
        deviceLanguage,  // Системный язык телефона
        language: dbLanguage,  // Язык из настроек БД
        skipSaveUser,  // true при fallback из streaming (сообщение уже сохранено)
      }),
    });
  }

  async clearAIChatHistory() {
    return this.request<{ success: boolean }>('/api/ai-chat/clear-history', {
      method: 'POST',
    });
  }

  /**
   * Streaming AI Chat - отправляет сообщение и получает ответ по частям
   * Использует react-native-sse для поддержки SSE в React Native
   * @param message - сообщение пользователя
   * @param deviceLanguage - язык устройства
   * @param dbLanguage - язык из настроек
   * @param onChunk - callback для каждого chunk контента
   * @param onComplete - callback при завершении
   * @param onError - callback при ошибке
   */
  async sendAIChatMessageStream(
    message: string,
    deviceLanguage: string | undefined,
    dbLanguage: string | undefined,
    onChunk: (content: string) => void,
    onComplete: (result: { fullContent: string; is_off_topic?: boolean; follow_ups?: string[]; detected_language?: string }) => void,
    onError: (error: { error: string; message: string }) => void
  ): Promise<void> {
    const token = await this.getToken();
    const url = `${this.config.apiBaseUrl}/api/ai-chat/stream`;
    
    return new Promise(async (resolve) => {
      let fullContent = '';
      let detectedLanguage = '';
      let isOffTopic = false;
      let completed = false;

      try {
        // Динамический импорт react-native-sse для совместимости
        const EventSource = (await import('react-native-sse')).default;
        
        const es = new EventSource(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            message,
            deviceLanguage,
            language: dbLanguage,
          }),
          timeout: 60000,
        });

        // Обработка события start
        es.addEventListener('start', (event: any) => {
          try {
            const data = JSON.parse(event.data);
            detectedLanguage = data.detected_language || '';
          } catch (e) {
            // Игнорируем ошибки парсинга
          }
        });

        // Обработка chunks контента
        es.addEventListener('content', (event: any) => {
          try {
            const data = JSON.parse(event.data);
            if (data.content) {
              fullContent += data.content;
              onChunk(data.content);
            }
          } catch (e) {
            // Игнорируем ошибки парсинга
          }
        });

        // Обработка завершения
        es.addEventListener('done', (event: any) => {
          try {
            const data = JSON.parse(event.data);
            isOffTopic = data.is_off_topic || false;
            detectedLanguage = data.detected_language || detectedLanguage;
            completed = true;
            
            onComplete({
              fullContent: data.fullContent || fullContent,
              is_off_topic: isOffTopic,
              follow_ups: data.follow_ups || [],
              detected_language: detectedLanguage,
            });
          } catch (e) {
            if (!completed && fullContent) {
              onComplete({ fullContent, is_off_topic: false, detected_language: detectedLanguage });
            }
          }
          es.close();
          resolve();
        });

        // Обработка ошибок от сервера
        es.addEventListener('error', (event: any) => {
          if (completed) return;
          
          try {
            const data = JSON.parse(event.data);
            completed = true;
            onError({ error: data.error || 'Error', message: data.message || 'Ошибка' });
          } catch (e) {
            // Ошибка соединения
            if (!completed) {
              completed = true;
              onError({ error: 'Connection error', message: 'Ошибка соединения' });
            }
          }
          es.close();
          resolve();
        });

        // Обработка закрытия соединения
        es.addEventListener('close', () => {
          if (!completed && fullContent) {
            onComplete({ fullContent, is_off_topic: isOffTopic, detected_language: detectedLanguage });
          }
          resolve();
        });

      } catch (importError) {
        // Если react-native-sse недоступен, возвращаем ошибку
        console.warn('SSE not available, streaming disabled:', importError);
        onError({ error: 'SSE unavailable', message: 'Streaming не поддерживается' });
        resolve();
      }
    });
  }

  // ==========================================
  // PLAID RECURRING TRANSACTIONS
  // ==========================================

  async getPlaidRecurringTransactions(): Promise<RecurringTransaction[]> {
    await this.ensureDevAuth();
    
    const headers: Record<string, string> = {};
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${this.config.apiBaseUrl}/api/plaid/recurring-transactions`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || 'Failed to get Plaid recurring transactions');
    }

    return response.json();
  }

  async updatePlaidRecurringReminder(
    plaidTransactionId: string,
    reminderData: {
      notification_time?: string;
      reminder_times?: string[];
      reminder_offsets?: number[];
      reminders_enabled?: boolean;
    }
  ): Promise<void> {
    await this.ensureDevAuth();
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${this.config.apiBaseUrl}/api/plaid/recurring-transactions/${plaidTransactionId}/reminders`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(reminderData),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || 'Failed to update reminder');
    }
  }

  async deletePlaidRecurringReminder(plaidTransactionId: string): Promise<void> {
    await this.ensureDevAuth();
    
    const headers: Record<string, string> = {};
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${this.config.apiBaseUrl}/api/plaid/recurring-transactions/${plaidTransactionId}/reminders`, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || 'Failed to delete reminder');
    }
  }

  async removePlaidRecurringTransaction(plaidTransactionId: string): Promise<void> {
    await this.ensureDevAuth();
    
    const headers: Record<string, string> = {};
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${this.config.apiBaseUrl}/api/plaid/recurring-transactions/${plaidTransactionId}`, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || 'Failed to remove from recurring');
    }
  }

  // ==========================================
  // EXPORT HISTORY
  // ==========================================

  async uploadExport(formData: FormData): Promise<any> {
    await this.ensureDevAuth();
    
    const headers: Record<string, string> = {};
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${this.config.apiBaseUrl}/api/export`, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || 'Upload failed');
    }

    return response.json();
  }

  async getExportDownloadUrl(exportId: number): Promise<string> {
    try {
      const { download_token } = await this.request<{ download_token: string; expires_in: number }>(
        `/api/export/${exportId}/download-token`,
        { method: 'POST' }
      );
      const url = new URL(`${this.config.apiBaseUrl}/downloads/${exportId}`);
      url.searchParams.set('token', download_token);
      return url.toString();
    } catch {
      // Fallback: use the base URL without a token so the server can
      // still prompt for auth via other means (e.g. cookie / redirect).
      return `${this.config.apiBaseUrl}/downloads/${exportId}`;
    }
  }

  async saveExportHistory(
    filename: string,
    periodText: string,
    exportConfig: any,
    fileSizeKb?: number
  ): Promise<any> {
    return this.request('/export-history', {
      method: 'POST',
      body: JSON.stringify({
        filename,
        period_text: periodText,
        export_config: exportConfig,
        file_size_kb: fileSizeKb,
      }),
    });
  }

  async getExportHistory(limit: number = 50): Promise<any> {
    return this.request(`/api/export-history?limit=${limit}`);
  }

  async deleteExportHistoryRecord(exportId: number): Promise<any> {
    return this.request(`/api/export-history/${exportId}`, {
      method: 'DELETE',
    });
  }

  // ==========================================
  // OAUTH PROVIDERS - Multi-Auth
  // ==========================================

  /**
   * Инициировать Google OAuth flow
   * @param isLinking - true если привязываем к существующему аккаунту
   * @returns URL для редиректа на Google
   */
  async initiateGoogleAuth(isLinking: boolean = false): Promise<{ auth_url: string }> {
    // Проверяем, запущено ли из Telegram WebApp (только для веба)
    const fromTelegram = this.telegram?.hasTelegram() || false;
    return this.get(`/api/auth/google/init?link=${isLinking}&fromTelegram=${fromTelegram}`);
  }

  /**
   * Получить информацию о текущем пользователе
   */
  async getUserInfo(): Promise<{ 
    user_id: number; 
    has_telegram_id: boolean; 
    telegram_id?: number;
    email?: string; 
    first_name?: string;
  }> {
    return this.get('/api/auth/user-info');
  }

  /**
   * Получить список привязанных OAuth провайдеров
   */
  async getOAuthProviders(): Promise<{ providers: any[]; count: number }> {
    return this.get('/api/auth/oauth/providers');
  }

  /**
   * Отвязать OAuth провайдер
   * @param provider - google, apple, facebook
   */
  async unlinkOAuthProvider(provider: string): Promise<{ success: boolean; message: string }> {
    return this.request(`/api/auth/oauth/${provider}`, {
      method: 'DELETE',
    });
  }

  /**
   * Сгенерировать код для привязки Telegram к текущему аккаунту
   */
  async generateTelegramLinkCode(): Promise<{
    code: string;
    expires_at: string;
    expires_in_minutes: number;
    instructions: string;
  }> {
    return this.post('/api/auth/telegram/generate-link-code');
  }

  // ==========================================
  // PASSWORD RESET
  // ==========================================

  /**
   * Запросить восстановление пароля (отправка email с ссылкой)
   * @param email - Email пользователя
   * @param recaptchaToken - Токен reCAPTCHA (опционально)
   */
  async requestPasswordReset(email: string, recaptchaToken?: string): Promise<{
    success: boolean;
    message: string;
  }> {
    const response = await fetch(`${this.config.apiBaseUrl}/api/auth/request-password-reset`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, recaptchaToken }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Ошибка запроса' }));
      throw new Error(errorData.error || 'Не удалось отправить запрос');
    }

    return response.json();
  }

  /**
   * Проверить валидность токена восстановления пароля
   * @param token - Токен из URL
   */
  async verifyResetToken(token: string): Promise<{ valid: boolean; error?: string }> {
    const response = await fetch(`${this.config.apiBaseUrl}/api/auth/verify-reset-token?token=${encodeURIComponent(token)}`, {
      method: 'GET',
    });

    return response.json();
  }

  /**
   * Сбросить пароль по токену
   * @param token - Токен восстановления
   * @param password - Новый пароль
   */
  async resetPassword(token: string, password: string): Promise<{
    success: boolean;
    message: string;
  }> {
    const response = await fetch(`${this.config.apiBaseUrl}/api/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, password }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Ошибка сброса пароля' }));
      throw new Error(errorData.error || 'Не удалось сбросить пароль');
    }

    return response.json();
  }

  // ==========================================
  // PLAID INTEGRATION
  // ==========================================

  /**
   * Создать link token для Plaid Link
   * @param countryCode - Код страны (US, CA, GB и т.д.)
   */
  async createPlaidLinkToken(countryCode?: string): Promise<{ link_token: string; expiration: string }> {
    return this.request('/api/plaid/create-link-token', {
      method: 'POST',
      body: JSON.stringify({ 
        clientName: 'Financial App',
        countryCode: countryCode || 'US',
      }),
    });
  }

  /**
   * Обменять public_token на access_token
   */
  async exchangePlaidPublicToken(publicToken: string): Promise<{
    success: boolean;
    item_id: string;
    institution_name: string;
    accounts_count: number;
  }> {
    return this.request('/api/plaid/exchange-public-token', {
      method: 'POST',
      body: JSON.stringify({ public_token: publicToken }),
    });
  }

  /**
   * Получить все подключенные банковские счета
   */
  async getPlaidAccounts(): Promise<{
    success: boolean;
    items: Array<{
      id: number;
      item_id: string;
      institution_name: string;
      status: string;
      created_at: string;
      last_successful_sync?: string;
    }>;
    accounts: Array<{
      id: number;
      item_id: string;
      account_id: string;
      name: string;
      official_name?: string;
      type: string;
      subtype: string;
      mask?: string;
      balance_current: number;
      balance_available: number;
      currency_code: string;
      institution_name: string;
      item_status: string;
      created_at: string;
    }>;
  }> {
    // Используем заголовки для предотвращения кэширования вместо query параметров
    return this.request('/api/plaid/accounts', {
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
      },
    });
  }

  /**
   * Синхронизировать транзакции для банка
   */
  async syncPlaidTransactions(itemId: string): Promise<{
    success: boolean;
    added: number;
    modified: number;
    removed: number;
    has_more: boolean;
    next_cursor?: string;
  }> {
    console.log('🔄 [syncPlaidTransactions] Requesting sync for item:', itemId);
    try {
      const result = await this.request<{
        success: boolean;
        added: number;
        modified: number;
        removed: number;
        has_more: boolean;
        next_cursor?: string;
      }>('/api/plaid/sync-transactions', {
        method: 'POST',
        body: JSON.stringify({ item_id: itemId }),
      });
      console.log('✅ [syncPlaidTransactions] Sync successful:', result);
      return result;
    } catch (error: any) {
      console.error('❌ [syncPlaidTransactions] Sync failed:', error);
      console.error('   Error message:', error.message);
      console.error('   Error response:', error.response);
      throw error;
    }
  }

  /**
   * Отключить банковский аккаунт (весь банк)
   * ⚠️ Это удалит ВСЕ транзакции из этого банка!
   */
  async disconnectPlaidAccount(itemId: string): Promise<{
    success: boolean;
    message: string;
    deletedTransactionsCount?: number;
  }> {
    return this.request(`/api/plaid/accounts/${itemId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Получить количество транзакций для банка (для предупреждения перед удалением)
   */
  async getPlaidItemTransactionCount(itemId: string): Promise<{
    success: boolean;
    transactionCount: number;
  }> {
    return this.request(`/api/plaid/item/${itemId}/transaction-count`, {
      method: 'GET',
    });
  }

  /**
   * Удалить отдельный счет из банка
   */
  async removePlaidAccount(itemId: string, accountId: string): Promise<{
    success: boolean;
    message: string;
  }> {
    return this.request(`/api/plaid/accounts/${itemId}/${accountId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Изменить флаг регулярности для транзакции Plaid
   */
  async updatePlaidTransactionRecurring(
    plaidTransactionId: string,
    isRecurring: boolean
  ): Promise<{
    success: boolean;
    message: string;
  }> {
    return this.request(`/api/plaid/transactions/${plaidTransactionId}/recurring`, {
      method: 'PUT',
      body: JSON.stringify({ is_recurring: isRecurring }),
    });
  }

  /**
   * Изменить флаг внутреннего перевода для транзакции Plaid
   */
  async updatePlaidTransactionInternalTransfer(
    plaidTransactionId: string,
    isInternalTransfer: boolean
  ): Promise<{
    success: boolean;
    message: string;
  }> {
    return this.request(`/api/plaid/transactions/${plaidTransactionId}/internal-transfer`, {
      method: 'PUT',
      body: JSON.stringify({ is_internal_transfer: isInternalTransfer }),
    });
  }

  /**
   * Обновить категорию Plaid транзакции
   */
  async updatePlaidTransactionCategory(
    plaidTransactionId: string,
    category: string,
    aiPredictedCategory?: string | null,
    contextNotes?: string | null
  ): Promise<{
    success: boolean;
    message: string;
  }> {
    return this.request(`/api/plaid/transactions/${plaidTransactionId}/category`, {
      method: 'PUT',
      body: JSON.stringify({
        category,
        ai_predicted_category: aiPredictedCategory || null,
        context_notes: contextNotes || null,
      }),
    });
  }

  /**
   * Переименовать Plaid счёт (задать custom_name).
   * Передать null чтобы сбросить на оригинальное имя.
   */
  async renamePlaidAccount(
    accountDbId: number,
    customName: string | null
  ): Promise<{
    success: boolean;
    account: { id: number; name: string; custom_name: string | null };
  }> {
    return this.request(`/api/plaid/accounts/${accountDbId}/rename`, {
      method: 'PUT',
      body: JSON.stringify({ custom_name: customName }),
    });
  }

  /**
   * Зарегистрировать устройство для push уведомлений
   */
  async registerDevice(deviceToken: string, platform: 'ios' | 'android'): Promise<{
    success: boolean;
    message: string;
  }> {
    return this.request('/api/devices/register', {
      method: 'POST',
      body: JSON.stringify({
        device_token: deviceToken,
        platform,
        device_type: 'mobile',
      }),
    });
  }

  // ==================== ONBOARDING ====================

  /**
   * Получить статус onboarding с сервера
   */
  async getOnboardingStatus(): Promise<{
    onboarding_completed: boolean;
  }> {
    try {
      return await this.request('/api/onboarding/status');
    } catch (error) {
      console.error('❌ Error getting onboarding status:', error);
      // В случае ошибки возвращаем false чтобы показать onboarding
      return { onboarding_completed: false };
    }
  }

  /**
   * Отметить onboarding как завершённый на сервере
   */
  async completeOnboarding(): Promise<{
    success: boolean;
    onboarding_completed: boolean;
  }> {
    return this.request('/api/onboarding/complete', {
      method: 'POST',
    });
  }

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // 🎨 User Category Colors
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  /**
   * Установить или обновить цвет категории для пользователя
   * @param id - UUID записи (для синхронизации с PowerSync)
   * @param categoryId - ID категории
   * @param color - HEX цвет (e.g. '#FF9F66')
   */
  async upsertCategoryColor(id: string, categoryId: string, color: string): Promise<{ success: boolean }> {
    return this.request('/api/categories/color', {
      method: 'PUT',
      body: JSON.stringify({ id, category_id: categoryId, color }),
    });
  }

  /**
   * Удалить пользовательский цвет категории (сбросить к дефолту)
   * @param categoryId - ID категории
   */
  async deleteCategoryColor(categoryId: string): Promise<{ success: boolean }> {
    return this.request(`/api/categories/color/${categoryId}`, {
      method: 'DELETE',
    });
  }
}
